import { Link } from 'react-router-dom';
import { VENDOR_MARK_DATE } from '@floorpoll/shared';
import { LegalPage } from './LegalPage.js';

export function Terms() {
  return (
    <LegalPage title="Terms of Service">
      <p>
        These terms are an agreement between you and BluuAlpha Technologies, LLC
        (&ldquo;BluuAlpha&rdquo;, &ldquo;we&rdquo;, &ldquo;us&rdquo;) covering your use of
        FloorPoll. Our <Link to="/privacy">Privacy Policy</Link> is part of them. By creating an
        account or using FloorPoll, you accept both.
      </p>

      <h2>1. What FloorPoll is</h2>
      <p>
        FloorPoll lets you build a poll, display it on a screen, and collect anonymous answers from
        an audience&rsquo;s phones in real time.
      </p>

      <h2>2. Your account</h2>
      <p>You need an account to create and present polls. Audience members do not.</p>
      <p>
        You must be at least 13 years old. You are responsible for what happens under your account,
        including keeping your password to yourself and telling us promptly at{' '}
        <a href="mailto:info@bluualpha.com">info@bluualpha.com</a> if you think someone else has
        access.
      </p>
      <p>
        Give us accurate details. An account registered on someone else&rsquo;s behalf is your
        responsibility, not theirs.
      </p>

      <h2>3. Workspaces and members</h2>
      <p>
        Polls live in workspaces. If you invite people to a workspace, you are giving them the
        access their role carries, and you are responsible for who you invite. Admins can change
        roles and remove members.
      </p>

      <h2>4. What you may not do</h2>
      <p>Do not use FloorPoll to:</p>
      <ul>
        <li>break the law, or help anyone else do so;</li>
        <li>post content you have no right to post;</li>
        <li>harass, threaten, or defame anyone;</li>
        <li>
          collect personal information from your audience through poll questions — the audience
          answers anonymously, and questions designed to identify individual respondents defeat that
          and are not permitted;
        </li>
        <li>probe, scan, or attack the service, or try to reach data that is not yours;</li>
        <li>automate voting, or otherwise manipulate results;</li>
        <li>resell or white-label the service without our written agreement.</li>
      </ul>

      <h2>5. Your content</h2>
      <p>Your polls are yours. Uploading them does not give us ownership.</p>
      <p>
        You do give us the permission we need to run the service: to store your content, and to
        display it to the audience and on the screen you present it on. That permission ends when
        you delete the content or close your account, except for backup copies that expire on their
        normal cycle.
      </p>
      <p>
        You confirm you have the right to use anything you upload, including logos and background
        images. We do not monitor content routinely, and we may remove content that breaks these
        terms.
      </p>

      <h2>6. The audience</h2>
      <p>
        Anyone with a join code can answer a poll. Codes are short by design, so treat a live poll
        as open to whoever can see the screen or learn the code. Do not use FloorPoll to collect
        answers you need to keep confidential.
      </p>
      <p>
        Votes are anonymous, and we provide no way for a poll&rsquo;s creator to identify an
        individual voter.
      </p>

      <h2>7. Availability</h2>
      <p>
        We aim to keep FloorPoll running and do not promise a specific level of uptime. We may
        change, suspend, or discontinue features, and will give reasonable notice of significant
        changes where we can.
      </p>
      <p>
        Before you rely on FloorPoll in front of a room, have a fallback. Live software and live
        audiences are an unforgiving combination.
      </p>

      <h2>8. Price</h2>
      <p>
        FloorPoll has a free plan and paid plans. The free plan lets you build polls and present
        them live, and holds up to 100 people in any one presentation. That count is per
        presentation, not per month: it starts again at zero every time you present, and someone who
        opens the join screen without voting is not counted against it.
      </p>
      <p>
        Paid plans lift the audience limit entirely rather than raising it to a larger number.
        Marquee is US$144 a year or US$15 a month. An Event Pass is US$39 once and carries the same
        features for 30 days without a subscription. Prices are in US dollars and exclude any tax we
        are required to add. The plan you are on, and what it includes, is shown on your account
        page.
      </p>
      <p>
        <strong>Your audience never pays.</strong> Answering a poll asks nobody in the room for an
        account, a card, or an app.
      </p>
      <p>
        <strong>The FloorPoll mark.</strong> From {VENDOR_MARK_DATE}, a poll presented on the free
        plan carries a small &ldquo;Powered by FloorPoll&rdquo; credit on the big screen and on your
        audience&rsquo;s phones, and a paid plan takes it off. What no plan takes off, because none
        of it is for sale: the join address shown on the screen, the address in your
        audience&rsquo;s browser, and the links to these documents under the join screen.
      </p>
      <p>
        <strong>Notice before a free feature is limited.</strong> If we put a new limit on something
        an existing free workspace can do today, we will email its admins first, and the limit will
        not take effect for at least 30 days after that email. New limits may apply immediately to
        workspaces created after we announce them.
      </p>
      <p>
        <strong>Billing and cancellation.</strong> Subscriptions renew automatically until you
        cancel. You can cancel at any time from your account page; your plan then runs to the end of
        the period you have already paid for and is not renewed. We do not refund part of a period
        that has already started, and an Event Pass is a single purchase for a fixed 30 days rather
        than a subscription. If we charge you in error, or the service is unavailable for a
        sustained period through our fault, contact us at{' '}
        <a href="mailto:info@bluualpha.com">info@bluualpha.com</a> and we will put it right. None of
        this affects rights you have under consumer law that cannot be waived by agreement.
      </p>
      <p>
        Payments are taken by Stripe, who handle the card details — they never pass through
        FloorPoll. See our <Link to="/privacy">Privacy Policy</Link> for what we hold about a paid
        account.
      </p>
      <p>
        If a payment fails, we keep your plan running for a short grace period while the payment is
        retried. If it is not resolved, the workspace returns to the free plan. Nothing you have
        created is deleted when that happens.
      </p>

      <h2>9. Ending it</h2>
      <p>
        You can stop using FloorPoll and close your account whenever you like by emailing{' '}
        <a href="mailto:info@bluualpha.com">info@bluualpha.com</a>.
      </p>
      <p>
        We may suspend or close an account that breaks these terms, that we are legally required to
        close, or that puts the service or its users at risk. Where circumstances allow, we will
        tell you why and give you a chance to put it right. Closing an account deletes its polls and
        results. Invoice and payment records are the one exception, because tax law requires us to
        keep them &mdash; see the <Link to="/privacy">Privacy Policy</Link> for how long.
      </p>

      <h2>10. Our intellectual property</h2>
      <p>
        FloorPoll — the software, the name, the design, the marks — belongs to BluuAlpha. These
        terms give you permission to use the service, not rights in it.
      </p>

      <h2>11. No warranty</h2>
      <p>
        FloorPoll is provided &ldquo;as is&rdquo; and &ldquo;as available&rdquo;, without warranties
        of any kind, whether express, implied, or statutory, including any implied warranties of
        merchantability, fitness for a particular purpose, and non-infringement. We do not warrant
        that the service will be uninterrupted, timely, secure, or error-free, or that results
        obtained through it will be accurate or reliable.
      </p>
      <p>
        Some jurisdictions do not allow the exclusion of implied warranties, so parts of this
        section may not apply to you.
      </p>

      <h2>12. Limitation of liability</h2>
      <p>
        To the fullest extent the law allows, BluuAlpha will not be liable for indirect, incidental,
        special, consequential, exemplary, or punitive damages, or for lost profits, revenue, data,
        or goodwill, arising out of or relating to your use of FloorPoll — whether the claim is in
        contract, tort, or any other theory, and whether or not we were told such damages were
        possible.
      </p>
      <p>
        Our total liability for all claims relating to the service is limited to the greater of the
        amount you paid us in the twelve months before the claim, or one hundred US dollars
        (US$100).
      </p>
      <p>
        Some jurisdictions do not allow these limits, so parts of this section may not apply to you.
        Nothing here limits liability that cannot lawfully be limited.
      </p>

      <h2>13. Indemnity</h2>
      <p>
        You agree to indemnify and hold BluuAlpha harmless from claims, damages, and reasonable
        legal costs arising from your content, your use of FloorPoll, or your breach of these terms.
      </p>

      <h2>14. Changes to these terms</h2>
      <p>
        We may update these terms. If we do, we will change the date at the top, and for material
        changes we will ask you to review and accept them the next time you sign in. If you do not
        accept, you can close your account; continuing to use FloorPoll after accepting means the
        updated terms apply.
      </p>

      <h2>15. Governing law</h2>
      <p>
        These terms are governed by the laws of the State of Alabama, USA, without regard to its
        conflict-of-laws rules. You and BluuAlpha agree that disputes will be heard exclusively in
        the state or federal courts sitting in Alabama, and each of us consents to their
        jurisdiction.
      </p>
      <p>
        If you are a consumer in a jurisdiction whose law gives you the right to bring proceedings
        locally, this section does not take that right away.
      </p>

      <h2>16. Everything else</h2>
      <p>
        If any part of these terms is held unenforceable, the rest stays in force. Our not enforcing
        a provision is not a waiver of it. You may not transfer your rights under these terms
        without our consent; we may transfer ours to a successor in connection with a merger,
        acquisition, or sale of assets.
      </p>
      <p>
        These terms and the <Link to="/privacy">Privacy Policy</Link> are the whole agreement
        between us about FloorPoll.
      </p>

      <h2>17. Contact</h2>
      <p>
        BluuAlpha Technologies, LLC — <a href="mailto:info@bluualpha.com">info@bluualpha.com</a>
      </p>
    </LegalPage>
  );
}
